/**
 * « il y a 2 jours » plutôt que « 23 oct. » : la fraîcheur fait la valeur d'un
 * signal. Sans dépendance, importable par les fronts.
 */
import { DEFAULT_LOCALE, INTL_LOCALE, messages, type Locale } from "@shared/i18n";

const formats = new Map<Locale, { relative: Intl.RelativeTimeFormat; full: Intl.DateTimeFormat; day: Intl.DateTimeFormat }>();

function formatsFor(locale: Locale) {
  let cached = formats.get(locale);
  if (!cached) {
    const intl = INTL_LOCALE[locale];
    cached = {
      relative: new Intl.RelativeTimeFormat(intl, { numeric: "auto" }),
      full: new Intl.DateTimeFormat(intl, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
        timeZone: "Europe/Paris",
      }),
      day: new Intl.DateTimeFormat(intl, {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
        timeZone: "Europe/Paris",
      }),
    };
    formats.set(locale, cached);
  }
  return cached;
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Une date ISO complète, ou seulement le jour (`2026-10-23`, typique de `observed_on`). */
export function parseDate(value: string | null | undefined) {
  if (!value) return null;
  const trimmed = value.trim();
  if (!/^\d{4}-\d{2}-\d{2}/.test(trimmed)) return null;
  const time = Date.parse(/^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? `${trimmed}T12:00:00` : trimmed);
  return Number.isNaN(time) ? null : { time, dayOnly: /^\d{4}-\d{2}-\d{2}$/.test(trimmed) };
}

export function relativeTime(time: number, now: number, dayOnly = false, locale: Locale = DEFAULT_LOCALE) {
  const { relative } = formatsFor(locale);
  const copy = messages(locale).common.time;
  const delta = time - now;
  const abs = Math.abs(delta);
  if (!dayOnly && abs < MINUTE) return copy.justNow;
  if (!dayOnly && abs < HOUR) return relative.format(Math.round(delta / MINUTE), "minute");
  if (!dayOnly && abs < DAY) return relative.format(Math.round(delta / HOUR), "hour");
  const days = Math.round(delta / DAY);
  if (dayOnly && days === 0) return copy.today;
  if (Math.abs(days) < 14) return relative.format(days, "day");
  if (Math.abs(days) < 60) return relative.format(Math.round(days / 7), "week");
  if (Math.abs(days) < 365) return relative.format(Math.round(days / 30), "month");
  return relative.format(Math.round(days / 365), "year");
}

/** La date exacte, pour l'infobulle. */
export function fullDate(time: number, dayOnly = false, locale: Locale = DEFAULT_LOCALE) {
  const { full, day } = formatsFor(locale);
  return (dayOnly ? day : full).format(new Date(time));
}
